<template>
    <div class="q-pa-md">
        <div class="filter-wrap row q-col-gutter-sm">
            <div class="col-shrink col-3">
                <q-select
                    v-model="filter.expiredState"
                    :options="expiredStates"
                    option-value="value"
                    option-label="label"
                    bg-color="white"
                    outlined
                    dense
                    emit-value
                    map-options
                    :label="$t('certificates.expiration')"
                    clearable
                    @input="resetPagination"
                />
            </div>
            <div v-if="!filter.expiredState" class="col-shrink col-3">
                <q-select
                    v-model="filter.perpetual"
                    :options="perpetualStates"
                    option-value="value"
                    option-label="label"
                    bg-color="white"
                    outlined
                    dense
                    emit-value
                    map-options
                    :label="$t('certificates.validity')"
                    clearable
                    @input="resetPagination"
                />
            </div>
            <div class="col">
                <q-input
                    v-model="filter.search"
                    :label="$t('general.search')"
                    bg-color="white"
                    debounce="300"
                    outlined
                    dense
                    clearable
                    @input="resetPagination"
                >
                    <template v-slot:append>
                        <q-icon name="search" />
                    </template>
                </q-input>
            </div>
        </div>
        <div class="q-gutter-md">
            <q-table
                :data="certificates"
                :columns="columns"
                :rows-per-page-options="rowsPerPageOptions"
                :pagination="pagination"
                :loading="isfetchingCertificates"
                row-key="id"
                flat
            >
                <template v-slot:body="props">
                    <!-- certificates row template -->
                    <q-tr :props="props">
                        <!-- certificate title -->
                        <q-td key="title" :props="props">
                            {{ props.row.title }}
                        </q-td>
                        <!-- certificate affiliate -->
                        <q-td key="user" :props="props">
                            {{ `${props.row.user.first_name}` }}
                        </q-td>
                        <!-- certificate issued date -->
                        <q-td key="issued_date" :props="props">
                            {{ formatDate(props.row.issued_date) }}
                        </q-td>
                        <!-- certificate valid start date -->
                        <q-td key="valid_start_date" :props="props">
                            {{ formatDate(props.row.valid_start_date) }}
                        </q-td>
                        <!-- certificate valid end date -->
                        <q-td key="valid_end_date" :props="props">
                            {{
                                props.row.perpetual
                                    ? $t('general.perpetual')
                                    : formatDate(props.row.valid_end_date)
                            }}
                        </q-td>
                        <!-- view and download certificate column -->
                        <q-td key="certificate" :props="props">
                            <q-btn
                                color="secondary"
                                style="width: 36px"
                                size="sm"
                                :icon="'download'"
                                unelevated
                                @click="getCertificate(props.row)"
                            />
                            <q-tooltip>{{ $t('actions.download') }}</q-tooltip>
                        </q-td>
                    </q-tr>
                </template>
            </q-table>
        </div>
        <!-- <div
            v-if="certificates.length === 0"
            class="q-pa-md"
        >
            <h6>{{ $t('profile.certificates_none') }}</h6>
            <q-btn
                class="q-my-md"
                to="/profile/my-courses"
                color="secondary"
            >
                {{$t('profile.my_courses')}}
            </q-btn>
        </div> -->
    </div>
</template>

<script>
import certificatesService from '@/services/modules/profile/certificates'
import pdf from '@/local-shared/mixins/pdf'
import formatDate from '@/helpers/date-time'
import store from '@/store'
import { base64StringToBlob } from 'blob-util'
import fileSaver from 'file-saver'
import _ from 'lodash'

const loadingKeys = {
    fetchingCertificates: 'fetching-certificates',
}

export default {
    name: 'Certificates',

    mixins: [pdf],

    data() {
        return {
            certificates: [],

            pagination: {
                sortBy: 'finishDate',
                descending: true,
                page: 1,
                rowsPerPage: 10,
            },

            rowsPerPageOptions: [5, 10, 25, 50, 100],

            filter: {
                search: '',
                expiredState: null,
                perpetual: null,
            },
        }
    },

    computed: {
        locale() {
            return store.getters['locale/currentLocale']
        },

        userLogged() {
            return store.getters['session/user']
        },

        columns() {
            if (this.userLogged.userRoles.name === 'client') {
                return [
                    {
                        name: 'title',
                        align: 'left',
                        label: this.$t('general.title'),
                        field: 'title',
                        sortable: true,
                    },
                    {
                        name: 'user',
                        align: 'left',
                        label: this.$t('general.affiliate'),
                        field: 'user',
                        sortable: true,
                    },
                    {
                        name: 'issued_date',
                        align: 'left',
                        label: this.$t('general.issued_date'),
                        field: 'issued_date',
                        sortable: true,
                    },
                    // { name: 'valid_start_date', align: 'left', label: this.$t('general.start_date'), field: 'valid_start_date', sortable: true },
                    {
                        name: 'valid_end_date',
                        align: 'left',
                        label: this.$t('general.expire_date'),
                        field: 'valid_end_date',
                        sortable: true,
                    },
                    {
                        name: 'certificate',
                        align: 'right',
                        label: this.$t('actions.download'),
                        field: 'certificate',
                        sortable: true,
                    },
                ]
            } else {
                return [
                    {
                        name: 'title',
                        align: 'left',
                        label: this.$t('general.title'),
                        field: 'title',
                        sortable: true,
                    },
                    {
                        name: 'issued_date',
                        align: 'left',
                        label: this.$t('general.issued_date'),
                        field: 'issued_date',
                        sortable: true,
                    },
                    // { name: 'valid_start_date', align: 'left', label: this.$t('general.start_date'), field: 'valid_start_date', sortable: true },
                    {
                        name: 'valid_end_date',
                        align: 'left',
                        label: this.$t('general.expire_date'),
                        field: 'valid_end_date',
                        sortable: true,
                    },
                    {
                        name: 'certificate',
                        align: 'right',
                        label: this.$t('actions.download'),
                        field: 'certificate',
                        sortable: true,
                    },
                ]
            }
        },

        expiredStates() {
            return [
                {
                    value: false,
                    label: this.$t('general.not_expired'),
                },
                {
                    value: true,
                    label: this.$t('general.is_expired'),
                },
            ]
        },

        perpetualStates() {
            return [
                {
                    value: true,
                    label: this.$t('general.perpetual'),
                },
                {
                    value: false,
                    label: this.$t('general.not_perpetual'),
                },
            ]
        },

        isLoading() {
            return this.isfetchingCertificates
        },

        isfetchingCertificates() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCertificates
            ).length
        },
    },

    async created() {
        await this.fetchCertificates()
    },

    methods: {
        resetPagination() {
            this.pagination.page = 1
            this.fetchCertificates()
        },

        async getCertificate(value) {
            let parameters = {
                filter: {
                    certificateId: value.id,
                },
            }

            let response = await certificatesService.generatePdf(parameters)
            let values = response.data.data[0].data

            if (values.file != null) {
                let aux = this.getBase64Server({
                    data: values.file,
                })

                let url = URL.createObjectURL(
                    base64StringToBlob(
                        _.split(aux, 'data:image/gif;base64,')[1]
                    )
                )

                fileSaver.saveAs(
                    url,
                    values.user.first_name +
                        '_' +
                        JSON.parse(values.course.title)[this.locale.key] +
                        '.png'
                )
            } else {
                await this.createPdf(
                    values.template,
                    false,
                    values.user,
                    values.teacher,
                    values.class,
                    values.course,
                    values.enrollment,
                    values.certificate
                )
            }
        },

        async fetchCertificates() {
            this.loadingAdd(loadingKeys.fetchingCertificates)

            let parameters = {
                filter: {
                    active: true,
                    search: this.filter.search,
                    userId: this.userLogged.id,
                    expiredState: this.filter.expiredState,
                    perpetualState: this.filter.perpetual,
                },
                relations: {
                    user: true,
                },
            }

            let response = await certificatesService.retrieve(parameters)

            if (response.status === 200) {
                this.certificates = response.data.results
                this.pagination.rowsNumber = response.data.pagination.total
            }

            this.loadingRemove(loadingKeys.fetchingCertificates)
        },

        formatDate(value) {
            return value ? formatDate.fromDatePickerToUxDate(value) : null
        },
    },
}
</script>
